import React, { useState } from "react";
import AlertBanner from "@bmi-digital/components/alert-banner";
import Form from "@bmi-digital/components/form";
import TextField from "@bmi-digital/components/text-field";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { useSiteContext } from "../Site";
import { convertMarkdownLinksToAnchorLinks } from "../FormSection";
import { useConfig } from "../../contexts/ConfigProvider";
import PasswordField from "./PasswordField";
import EmailField from "./EmailField";
import {
  classes,
  StyledAlertBannerWrapper,
  StyledFormElementsContainer,
  StyledCheckbox,
  StyledGridContainer,
  StyledProgressIndicator
} from "./styles/EmbeddedRegistrationFormStyles";
import useSubmitForm from "./hooks/useSubmitForm";
import { FieldNames } from "./types";
import type { EmailStatus, EmbeddedRegistrationFormProps } from "./types";

const EmbeddedRegistrationFormSection = ({
  redirectURL,
  backgroundColor
}: EmbeddedRegistrationFormProps) => {
  const [isPasswordCorrect, setIsPasswordCorrect] = useState<boolean>(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>({
    alreadyExists: false,
    needsVerification: false
  });
  const [email, setEmail] = useState<string | undefined>(undefined);
  const { getMicroCopy, payloadPageId } = useSiteContext();
  const { handleSubmit, isSubmitting } = useSubmitForm({
    setEmailStatus,
    redirectURL
  });
  const { spaceMarketCode } = useConfig();
  const submitButtonLabel = getMicroCopy(
    microCopy.REGISTRATION_FORM_SUBMIT_BUTTON_TEXT
  );

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        data-testid="embedded-registration-form"
        noValidate
        id={`${spaceMarketCode.toUpperCase()}___PageID_${payloadPageId}`}
      >
        <StyledFormElementsContainer>
          <StyledGridContainer>
            <div>
              <TextField
                name={FieldNames.firstName}
                label={getMicroCopy(
                  microCopy.REGISTRATION_FORM_FIRSTNAME_FIELD_LABEL
                )}
                data-testid="embedded-registration-form-firstName-field"
                variant="hybrid"
                isRequired
                fieldIsRequiredError={getMicroCopy(
                  microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
                )}
                fullWidth
              />
            </div>
            <div>
              <TextField
                name={FieldNames.lastName}
                label={getMicroCopy(
                  microCopy.REGISTRATION_FORM_LASTNAME_FIELD_LABEL
                )}
                variant="hybrid"
                data-testid="embedded-registration-form-lastName-field"
                isRequired
                fieldIsRequiredError={getMicroCopy(
                  microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
                )}
                fullWidth
              />
            </div>
            <div className={classes.gridFullWidth}>
              <EmailField
                alreadyExists={emailStatus.alreadyExists}
                needsVerification={emailStatus.needsVerification}
                lastBrokenEmail={emailStatus.lastBrokenEmail}
                value={email}
                setValue={setEmail}
              />
            </div>
            <div className={classes.gridFullWidth}>
              <PasswordField setIsPasswordCorrect={setIsPasswordCorrect} />
            </div>
            <div className={classes.gridFullWidth}>
              <StyledCheckbox
                isRequired
                name={FieldNames.termsOfUse}
                data-testid="embedded-registration-form-terms-of-use-field"
                label={convertMarkdownLinksToAnchorLinks(
                  getMicroCopy(
                    microCopy.REGISTRATION_FORM_EMAIL_TERMS_OF_USE_LABEL
                  )
                )}
                fieldIsRequiredError={getMicroCopy(
                  microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
                )}
                className={classes.termsAndConditionsCheckbox}
                asteriskPosition="trailing"
              />
              <StyledCheckbox
                isRequired
                name={FieldNames.privacyPolicy}
                data-testid="embedded-registration-form-privacy-policy-field"
                label={convertMarkdownLinksToAnchorLinks(
                  getMicroCopy(
                    microCopy.REGISTRATION_FORM_EMAIL_PRIVACY_POLICY_LABEL
                  )
                )}
                fieldIsRequiredError={getMicroCopy(
                  microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
                )}
                className={classes.termsAndConditionsCheckbox}
                asteriskPosition="trailing"
              />
            </div>
          </StyledGridContainer>
          <Form.SubmitButton
            fullWidth
            disabled={!isPasswordCorrect || isSubmitting}
            data-testid="embedded-registration-form-submit-button"
            endIcon={
              isSubmitting && (
                <StyledProgressIndicator size={24} color="inherit" />
              )
            }
            gtm={{
              id: "cta-click1",
              event: "register_button",
              label: submitButtonLabel,
              ...(redirectURL && { action: redirectURL })
            }}
            hasDarkBackground={backgroundColor === "Blue"}
          >
            {submitButtonLabel}
          </Form.SubmitButton>
        </StyledFormElementsContainer>
      </Form>
      {(emailStatus.alreadyExists || emailStatus.needsVerification) &&
      email &&
      email === emailStatus.lastBrokenEmail ? (
        <StyledAlertBannerWrapper>
          <AlertBanner severity="warning">
            {emailStatus.alreadyExists
              ? getMicroCopy(
                  microCopy.REGISTRATION_FORM_EMAIL_EXISTS_ERROR_TEXT
                )
              : getMicroCopy(
                  microCopy.REGISTRATION_FORM_EMAIL_VERIFICATION_NEEDED_ERROR_TEXT
                )}
          </AlertBanner>
        </StyledAlertBannerWrapper>
      ) : null}
    </>
  );
};

export default EmbeddedRegistrationFormSection;
